import React, { useRef, useState } from 'react';
import { TextField, Button, Box, Typography, Dialog, CircularProgress, Zoom, DialogContent, Alert } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import DOMPurify from 'dompurify';
import { EqForm } from '../../../hooks/apiHandler';
import { CheckCircleOutline } from '@mui/icons-material';

const ContactForm = ({ onClose }) => {
  const intFormData = {
    name: '',
    email: '',
    phone: '',
    cityName: '',
    businessName: '',
  };
  const [formData, setFormData] = useState(intFormData);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [statusMsg, setStatusMsg] = useState({ show: false, text: '', type: 'info' });
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const recaptchaRef = useRef(null);

  // Rate limiting: 30 seconds between submissions
  const RATE_LIMIT_MS = 30000;

  // Enforce HTTPS in production
  const checkSecureConnection = () => {
    if (process.env.NODE_ENV === 'production' && window.location.protocol !== 'https:') {
      setStatusMsg({ 
        show: true, 
        text: 'Secure connection required. Please use HTTPS.', 
        type: 'error' 
      });
      return false;
    }
    return true;
  };

  // Sanitize input to prevent XSS
  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input.trim(), { 
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [] 
    });
  };

  // Validate input format
  const validateInput = (data) => {
    const errors = [];

    // Name validation (2-100 characters, letters and spaces only)
    if (!/^[a-zA-Z\s]{2,100}$/.test(data.name)) {
      errors.push('Name must be 2-100 characters and contain only letters.');
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(data.email) || data.email.length > 254) {
      errors.push('Please enter a valid email address.');
    }

    // Phone validation (10-15 digits, international format allowed)
    if (!/^[\d\s+()-]{10,15}$/.test(data.phone)) {
      errors.push('Phone number must be 10-15 digits.');
    }

    // City validation (2-100 characters)
    if (data.cityName.length < 2 || data.cityName.length > 100) {
      errors.push('City name must be 2-100 characters.');
    }

    // Business name validation (2-200 characters)
    if (data.businessName.length < 2 || data.businessName.length > 200) {
      errors.push('Business name must be 2-200 characters.');
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input on change
    const sanitizedValue = sanitizeInput(value);
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const handleCaptcha = (token) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check HTTPS
    if (!checkSecureConnection()) {
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmitTime < RATE_LIMIT_MS) {
      const remainingSeconds = Math.ceil((RATE_LIMIT_MS - (now - lastSubmitTime)) / 1000);
      setStatusMsg({ 
        show: true, 
        text: `Please wait ${remainingSeconds} seconds before submitting again.`, 
        type: 'warning' 
      });
      return;
    }

    // Captcha validation
    if (!captchaToken) {
      setStatusMsg({ 
        show: true, 
        text: 'Please complete the reCAPTCHA verification.', 
        type: 'warning' 
      });
      return;
    }

    // Input validation
    const validationErrors = validateInput(formData);
    if (validationErrors.length > 0) {
      setStatusMsg({ 
        show: true, 
        text: validationErrors.join(' '), 
        type: 'error' 
      });
      return;
    }

    setLoading(true);
    setStatusMsg({ show: false, text: '', type: 'info' });

    try {
      // Sanitize all data before sending
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        phone: sanitizeInput(formData.phone),
        cityName: sanitizeInput(formData.cityName),
        businessName: sanitizeInput(formData.businessName),
        captchaToken
      };

      const response = await EqForm(sanitizedData);
      
      // Set rate limit timestamp
      setLastSubmitTime(Date.now());
      setLoading(false);
      setSuccess(true);

      // Wait for 5 seconds, then close and trigger parent callback
      setTimeout(() => {
        setSuccess(false);
        setFormData(intFormData);
        setCaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        if (onClose) onClose();
      }, 5000);
    } catch (error) {
      setLoading(false);
      setStatusMsg({ 
        show: true, 
        text: 'Unable to submit form. Please try again later.', 
        type: 'error' 
      });
      // Reset captcha on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mx: 'auto',
        p: 4,
        borderRadius: 2
      }}
    >
      {statusMsg.show && (
        <Alert
          severity={statusMsg.type} 
          onClose={() => setStatusMsg({ ...statusMsg, show: false })}
          sx={{ borderRadius: '12px', mb: 1 }}
        >
          {statusMsg.text}
        </Alert>
      )}
      <TextField
        sx={{ borderRadius: '20px !important' }}
        name="name"
        label="Name"
        placeholder="Enter your name"
        value={formData.name}
        onChange={handleChange}
        required
      />
      <TextField
        sx={{ borderRadius: '20px !important' }}
        name="email"
        label="Email"
        type="email"
        placeholder="Enter your email"
        value={formData.email}
        onChange={handleChange}
        required
      />
      <TextField
        sx={{ borderRadius: '20px !important' }}
        name="phone"
        label="Phone Number"
        type="tel"
        placeholder="Enter your phone number"
        value={formData.phone}
        onChange={handleChange}
        required
      />
      <TextField
        sx={{ borderRadius: '20px !important' }}
        name="cityName"
        label="City Name"
        placeholder="Enter your City Name"
        value={formData.cityName}
        onChange={handleChange}
        required
      />
      <TextField
        sx={{ borderRadius: '20px !important' }}
        name="businessName"
        label="Business Name"
        placeholder="Enter your Business Name"
        value={formData.businessName}
        onChange={handleChange}
        required
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey="6LfAJIsrAAAAAMYx3GqDOPPYzZsGKasAj_xPkQwU"
          onChange={handleCaptcha}
        />
      </Box>

      <Button
        sx={{
          background: 'tomato',
          borderRadius: '20px',
          py: 1.5,
          fontWeight: 'bold',
          transition: '0.3s',
          '&:hover': { background: '#e5634d', transform: 'translateY(-2px)' }
        }}
        type="submit"
        variant="contained"
        disabled={loading || success}
        fullWidth
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Request"}
      </Button>

      <Typography variant="caption" color="text.secondary" align="center">
        Our specialist will contact you to set up your free demo.
      </Typography>

      {/* SUCCESS DIALOG (5 SECOND DURATION) */}
      <Dialog
        open={success}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            textAlign: 'center',
            p: 4,
            maxWidth: '380px'
          }
        }}
      >
        <DialogContent>
          <CheckCircleOutline sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: 'text.primary' }}>
            Request Successful!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for choosing MiiRA POS. Our expert will contact you within the next 24 hours to schedule your demo.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            This message will close automatically...
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
export default ContactForm;
